/**
 * Theme provider — Light / Dark / Auto.
 *
 * v165 — `auto` is CLOCK-FIRST: day-time (06:00–18:00 local) = light,
 * else dark. We deliberately ignore OS `prefers-color-scheme` here so
 * the experience matches "day mode at day time" even on phones that
 * are permanently in OS dark mode. The user can still pick light/dark
 * explicitly via <ThemeToggle /> and that survives reloads.
 *
 * Bootstrap sync (v163): the `dark` class is stamped on <html> by a
 * tiny inline script in `public/index.html` BEFORE React mounts, so
 * there's no FOUC (flash of unstyled content). This module reads the
 * current state from the DOM on first render so the React tree
 * agrees with what the browser already painted.
 */
import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

const ThemeCtx = createContext(null);
const KEY = 've_theme_mode';

/* Resolve 'auto' mode — clock-first; day = 06:00–18:00 local. */
function resolveAuto() {
  const h = new Date().getHours();
  return (h >= 6 && h < 18) ? 'light' : 'dark';
}

function apply(mode) {
  const effective = mode === 'auto' ? resolveAuto() : mode;
  const root = document.documentElement;
  if (effective === 'dark') root.classList.add('dark');
  else root.classList.remove('dark');
  root.setAttribute('data-theme', effective);
  return effective;
}

/* Read the initial effective theme directly from the DOM. The inline
 * script in index.html has already set the class — trust that single
 * source of truth so first render matches first paint exactly. */
function initialEffective() {
  if (typeof document === 'undefined') return 'light';
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
}

export function ThemeProvider({ children }) {
  const [mode, setMode] = useState(() =>
    (typeof localStorage !== 'undefined' && localStorage.getItem(KEY)) || 'auto'
  );
  const [effective, setEffective] = useState(initialEffective);

  /* eslint-disable -- react-hooks/set-state-in-effect false-positive on theme apply */
  useEffect(() => {
    setEffective(apply(mode));
    localStorage.setItem(KEY, mode);
  }, [mode]);

  // Re-evaluate in auto mode on a clock tick (every minute, in case
  // the day/night boundary crosses while the page is open). v165:
  // dropped the OS-preference listener — clock is the single source
  // of truth so a phone stuck in dark mode still sees light at noon.
  useEffect(() => {
    if (mode !== 'auto') return undefined;
    const tick = () => setEffective(apply('auto'));
    const interval = setInterval(tick, 60_000);
    return () => clearInterval(interval);
  }, [mode]);
  /* eslint-enable */

  const cycle = useCallback(() => {
    setMode((m) => (m === 'light' ? 'dark' : m === 'dark' ? 'auto' : 'light'));
  }, []);

  const value = useMemo(() => ({ mode, effective, setMode, cycle }), [mode, effective, cycle]);
  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

export const useTheme = () => useContext(ThemeCtx);
